import { Node, mergeAttributes } from '@tiptap/core'
import { ReactNodeViewRenderer } from '@tiptap/react'

import { RunbookRefMentionNodeView } from './runbook-ref-mention-node-view'

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    runbookRefMention: {
      insertRunbookRefMention: (options: { ref: string }) => ReturnType
    }
  }
}

export interface RunbookRefMentionAttrs {
  /** Canonical ref string, e.g. `@params.region` or `@step.s_aws.users[0].email` */
  ref: string
}

/**
 * RFC-167 §5.7.4 / §5.8.1: inline atom representing an `@`-ref to a
 * RunbookParam, step output, run meta, or env binding. Renders as a
 * stylized pill via the NodeView; serializes to / from the bare
 * `@scope.path` string in markdown.
 */
export const RunbookRefMentionNode = Node.create({
  name: 'runbookRefMention',

  priority: 1000,

  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  draggable: false,

  addAttributes() {
    return {
      ref: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-ref') || '',
        renderHTML: (attributes) => ({ 'data-ref': attributes.ref }),
      },
    }
  },

  parseHTML() {
    return [{ tag: 'span[data-runbook-ref]' }]
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(HTMLAttributes, { 'data-runbook-ref': '' }),
      HTMLAttributes['data-ref'] || '',
    ]
  },

  addCommands() {
    return {
      insertRunbookRefMention:
        ({ ref }) =>
        ({ commands }) =>
          commands.insertContent({
            type: this.name,
            attrs: { ref },
          }),
    }
  },

  addNodeView() {
    return ReactNodeViewRenderer(RunbookRefMentionNodeView)
  },
})
